// Weather (PLR-10; §5.5 phase 1). PURE + isomorphic: a small global state machine
// advanced each tick from the world's deterministic rng, so client, server, and
// tests agree on when it rains/thunders. Whether rain falls as snow is a per-biome
// rendering decision (cold biomes) the client makes; the sim state is global —
// matching Minecraft, where weather is world-wide.
import { WEATHER } from '../config';
import type { Rng } from './rng';

export interface WeatherState {
  raining: boolean;
  thundering: boolean;
  /** Ticks remaining in the current rain/clear phase (0 → flip). */
  rainCountdown: number;
  /** Ticks remaining in the current thunder phase (only meaningful while raining). */
  thunderCountdown: number;
}

const between = (rng: Rng, min: number, max: number): number => min + rng.nextInt(Math.max(1, max - min + 1));

export function initialWeather(): WeatherState {
  return { raining: false, thundering: false, rainCountdown: WEATHER.CLEAR_MIN, thunderCountdown: WEATHER.THUNDER_CLEAR_MIN };
}

/**
 * Advance the weather one tick. When `doWeatherCycle` is off the countdowns
 * freeze (so `/weather` holds). Rain and thunder run independent countdowns:
 * thunder only "shows" while it is also raining.
 */
export function tickWeather(w: WeatherState, rng: Rng, doWeatherCycle: boolean): void {
  if (!doWeatherCycle) return;
  if (--w.rainCountdown <= 0) {
    w.raining = !w.raining;
    w.rainCountdown = w.raining ? between(rng, WEATHER.RAIN_MIN, WEATHER.RAIN_MAX) : between(rng, WEATHER.CLEAR_MIN, WEATHER.CLEAR_MAX);
  }
  if (--w.thunderCountdown <= 0) {
    w.thundering = !w.thundering;
    w.thunderCountdown = w.thundering ? between(rng, WEATHER.THUNDER_MIN, WEATHER.THUNDER_MAX) : between(rng, WEATHER.THUNDER_CLEAR_MIN, WEATHER.THUNDER_CLEAR_MAX);
  }
}

/** `/weather clear|rain|thunder [duration]`. Sets the state and a countdown. */
export function setWeather(w: WeatherState, kind: 'clear' | 'rain' | 'thunder', durationTicks?: number): void {
  if (kind === 'clear') {
    w.raining = false; w.thundering = false;
    w.rainCountdown = durationTicks ?? WEATHER.CLEAR_MIN;
    w.thunderCountdown = durationTicks ?? WEATHER.THUNDER_CLEAR_MIN;
  } else {
    w.raining = true;
    w.thundering = kind === 'thunder';
    w.rainCountdown = durationTicks ?? WEATHER.RAIN_MIN;
    w.thunderCountdown = durationTicks ?? (kind === 'thunder' ? WEATHER.THUNDER_MIN : WEATHER.THUNDER_CLEAR_MIN);
  }
}

/** A thunderstorm = raining AND thundering (lightning + hostile spawns at day). */
export function isThunderstorm(w: WeatherState): boolean {
  return w.raining && w.thundering;
}
